/**
 * @fileoverview 配乗計画画面
 * @description 「どのクルーを、どの日に、どの船へ配乗するか」を計画する画面。
 *              クルー×日のグリッドで、セルには配乗先の船舶（または待機／休み）を設定する。
 *              保存時に休息不足の警告（warnings）を表示し、
 *              GET /shifts/violations で計画段階の休息規制抵触予定を検知する。
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, AlertTriangle, X, FileText, FileSpreadsheet, Ship, CalendarClock } from 'lucide-react';
import * as api from '../services/api';
import { exportSimpleList } from '../utils/excelHelper';
import SearchableSelect from '../components/SearchableSelect';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
const SUBDOMAIN = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();

interface Shift {
  id: string;
  user_id: string;
  user_name: string;
  vessel_id: string | null;
  vessel_name: string | null;
  shift_date: string;
  planned_start_at: string | null;
  planned_end_at: string | null;
  shift_type: 'work' | 'standby' | 'off' | string;
  status: string;
  note: string | null;
}
interface CrewUser { id: string; user_name: string; user_role: string; }
interface Vessel   { id: string; vessel_name: string; }
interface Violation { user_id: string; user_name: string; shift_date: string; issue: string; rest_hours: number; }
interface Assignment { user_id: string; vessel_id: string; vessel_name: string; assignment_status: string; }

type ShiftType = 'work' | 'standby' | 'off';
const TYPE_LABEL: Record<ShiftType, string> = { work: '乗船', standby: '待機', off: '休み' };
const DOW = ['日', '月', '火', '水', '木', '金', '土'];
const VESSEL_COLORS = ['#2563eb', '#059669', '#d97706', '#db2777', '#7c3aed', '#0891b2', '#65a30d', '#e11d48'];

// YYYY-MM-DD（ローカル）
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// 月曜始まりの週頭
function startOfWeek(d: Date) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = x.getDay();          // 0=日,1=月,...
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}
// 保存値（ISO/日時文字列）から HH:MM を取り出す
const timeOf = (v?: string | null): string => { if (!v) return ''; const m = /[T\s](\d{2}:\d{2})/.exec(v); return m?.[1] ?? ''; };
// 日付 + HH:MM を日時文字列に
const toDateTime = (date: string, time: string) => (time ? `${date}T${time}:00` : undefined);
// 警告要素を文字列化（文字列/オブジェクト両対応）
const warnText = (w: any): string => (typeof w === 'string' ? w : (w?.message ?? JSON.stringify(w)));

interface FormState {
  id: string | null;
  user_id: string;
  vessel_id: string;
  shift_date: string;
  start: string;
  end: string;
  shift_type: ShiftType;
  note: string;
}
const emptyForm = (date: string, user_id = '', vessel_id = ''): FormState =>
  ({ id: null, user_id, vessel_id, shift_date: date, start: '', end: '', shift_type: 'work', note: '' });

// 実装本体（後日リリース時に下部の CrewDeploymentPage から呼び出す）
const CrewDeploymentPageImpl: React.FC = () => {
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [vesselFilter, setVesselFilter] = useState('');   // '' = 全船舶
  const [shifts, setShifts]         = useState<Shift[]>([]);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [users, setUsers]           = useState<CrewUser[]>([]);
  const [vessels, setVessels]       = useState<Vessel[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading]       = useState(true);
  const [pdfLoading, setPdfLoading]     = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm]           = useState<FormState>(emptyForm(ymd(new Date())));
  const [saving, setSaving]       = useState(false);
  const [modalError, setModalError]     = useState('');
  const [modalWarnings, setModalWarnings] = useState<string[]>([]);

  // 保存成功トースト（警告つき）
  const [toast, setToast] = useState<{ msg: string; warnings: string[] } | null>(null);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = days[6]!;

  // マスタ（クルー・船舶・現在の配属）読み込み
  useEffect(() => {
    (async () => {
      try {
        const [uRes, vRes, aRes] = await Promise.all([
          api.get<any>('/users'),
          api.get<any>('/vessels'),
          api.get<any>('/vessels/assignments/list'),
        ]);
        const rawUsers: CrewUser[] = uRes?.users ?? uRes?.data ?? [];
        setUsers(rawUsers.filter(u => u.user_role === 'crew' || u.user_role === 'captain'));
        setVessels(vRes?.vessels ?? vRes?.data ?? []);
        setAssignments(aRes?.data ?? []);
      } catch (e) { console.error('[CrewDeploymentPage/master]', e); }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const start = ymd(weekStart), end = ymd(weekEnd);
    try {
      const q: Record<string, string> = { start, end };
      if (vesselFilter) q.vessel_id = vesselFilter;
      const [sRes, vioRes] = await Promise.all([
        api.get<any>('/shifts', q),
        api.get<any>('/shifts/violations', { start, end }),
      ]);
      setShifts(sRes?.data ?? []);
      setViolations(vioRes?.data ?? []);
    } catch (e) { console.error('[CrewDeploymentPage]', e); setShifts([]); setViolations([]); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, vesselFilter]);

  useEffect(() => { load(); }, [load]);

  // 乗組員の現在の乗船船舶（配属マスタ基準）
  const baselineOf = useCallback(
    (userId: string) => assignments.find(a => a.user_id === userId && a.assignment_status === 'on_board'),
    [assignments],
  );

  // 船舶ごとの表示色（一覧内のインデックスで固定割り当て）
  const vesselColor = useCallback((vesselId: string | null | undefined) => {
    if (!vesselId) return '#64748b';
    const idx = vessels.findIndex(v => v.id === vesselId);
    return VESSEL_COLORS[idx >= 0 ? idx % VESSEL_COLORS.length : 0] ?? '#64748b';
  }, [vessels]);

  // グリッド行に表示するクルー: 特定船舶を選んでいる場合は
  // 「現在その船に乗船中」または「その期間にその船への配乗予定がある」クルーのみに絞る
  const crewRows = useMemo(() => {
    if (!vesselFilter) return users;
    const onBoardIds = new Set(
      assignments.filter(a => a.vessel_id === vesselFilter && a.assignment_status === 'on_board').map(a => a.user_id)
    );
    const scheduledIds = new Set(shifts.filter(s => s.vessel_id === vesselFilter).map(s => s.user_id));
    return users.filter(u => onBoardIds.has(u.id) || scheduledIds.has(u.id));
  }, [users, assignments, shifts, vesselFilter]);

  const shiftAt = (userId: string, dateIso: string) =>
    shifts.find(s => s.user_id === userId && (s.shift_date ?? '').slice(0, 10) === dateIso);

  // 日付ごとの配乗人数サマリー（船舶別）
  const manningByDate = useMemo(() => {
    return days.map(d => {
      const dateIso = ymd(d);
      const counts = new Map<string, number>();
      for (const s of shifts) {
        if ((s.shift_date ?? '').slice(0, 10) !== dateIso) continue;
        if (s.shift_type !== 'work' || !s.vessel_id) continue;
        counts.set(s.vessel_id, (counts.get(s.vessel_id) ?? 0) + 1);
      }
      return counts;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
  }, [shifts, weekStart]);

  const openNew = (date?: string, userId?: string) => {
    setModalError(''); setModalWarnings([]);
    const uid = userId ?? '';
    const defaultVessel = vesselFilter || (uid ? baselineOf(uid)?.vessel_id ?? '' : '');
    setForm(emptyForm(date ?? ymd(weekStart), uid, defaultVessel));
    setModalOpen(true);
  };
  const openEdit = (s: Shift) => {
    setModalError(''); setModalWarnings([]);
    setForm({
      id: s.id,
      user_id: s.user_id,
      vessel_id: s.vessel_id ?? '',
      shift_date: (s.shift_date ?? '').slice(0, 10),
      start: timeOf(s.planned_start_at),
      end: timeOf(s.planned_end_at),
      shift_type: (['work', 'standby', 'off'].includes(s.shift_type) ? s.shift_type : 'work') as ShiftType,
      note: s.note ?? '',
    });
    setModalOpen(true);
  };

  const save = async () => {
    setModalError(''); setModalWarnings([]);
    if (!form.user_id)   { setModalError('クルーを選択してください'); return; }
    if (!form.shift_date) { setModalError('日付を選択してください'); return; }
    if (form.shift_type === 'work' && !form.vessel_id) { setModalError('配乗する船舶を選択してください'); return; }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        user_id: form.user_id,
        vessel_id: form.shift_type === 'work' ? (form.vessel_id || undefined) : undefined,
        shift_date: form.shift_date,
        planned_start_at: toDateTime(form.shift_date, form.start),
        planned_end_at:   toDateTime(form.shift_date, form.end),
        shift_type: form.shift_type,
        note: form.note || undefined,
      };
      const res = form.id
        ? await api.put<any>(`/shifts/${form.id}`, body)
        : await api.post<any>('/shifts', body);
      const warnings: string[] = (res?.warnings ?? []).map(warnText);
      await load();
      if (warnings.length > 0) {
        // 保存は成功。警告はモーダル上部＋トーストで表示し、モーダルは閉じない
        setModalWarnings(warnings);
        setToast({ msg: '保存しました（休息規制の警告あり）', warnings });
      } else {
        setModalOpen(false);
        setToast({ msg: '保存しました', warnings: [] });
      }
    } catch (e: any) {
      setModalError(e.message ?? '保存に失敗しました');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!form.id) return;
    if (!window.confirm('この配乗予定を削除しますか？')) return;
    setSaving(true);
    try {
      await api.del(`/shifts/${form.id}`);
      await load();
      setModalOpen(false);
      setToast({ msg: '配乗予定を削除しました', warnings: [] });
    } catch (e: any) {
      setModalError(e.message ?? '削除に失敗しました');
    }
    setSaving(false);
  };

  // PDF出力（バックエンド生成）: GET /shifts/pdf?start=&end=&vessel_id=
  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const start = ymd(weekStart), end = ymd(weekEnd);
      const token = api.getToken() ?? '';
      const url = `${API_BASE}/shifts/pdf?start=${start}&end=${end}${vesselFilter ? `&vessel_id=${vesselFilter}` : ''}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': SUBDOMAIN, 'X-App-Type': 'admin' } });
      if (!r.ok) throw new Error('PDF生成失敗');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `配乗計画表_${start}〜${end}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      alert(e?.message ?? 'PDF出力に失敗しました');
    }
    setPdfLoading(false);
  };

  // Excel出力（フロント生成）: 表示中の期間の配乗一覧
  const exportExcel = async () => {
    setExcelLoading(true);
    try {
      const start = ymd(weekStart), end = ymd(weekEnd);
      const typeLabel = (t: string) => TYPE_LABEL[t as ShiftType] ?? t;
      const rows = shifts.map(s => [
        s.user_name,
        (s.shift_date ?? '').slice(0, 10),
        timeOf(s.planned_start_at),
        timeOf(s.planned_end_at),
        typeLabel(s.shift_type),
        s.vessel_name ?? '',
        s.status ?? '',
      ]);
      await exportSimpleList(
        [
          { label: 'クルー', width: 20 },
          { label: '日付', width: 14 },
          { label: '開始', width: 10 },
          { label: '終了', width: 10 },
          { label: '状態', width: 10 },
          { label: '配乗船舶', width: 20 },
          { label: '状態(承認)', width: 12 },
        ],
        rows,
        '配乗',
        `配乗計画表_${start}〜${end}.xlsx`,
        `配乗計画表（${start}〜${end}）`,
      );
    } catch (e: any) {
      alert(e?.message ?? 'Excel出力に失敗しました');
    }
    setExcelLoading(false);
  };

  const rangeLabel = `${weekStart.getMonth() + 1}/${weekStart.getDate()}〜${weekEnd.getMonth() + 1}/${weekEnd.getDate()}`;

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <div>
          <h1 className="page-title">配乗計画</h1>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#94a3b8' }}>乗組員をどの日にどの船へ配乗するかを計画します</p>
        </div>
        <div className="flex-row" style={{ gap: 8 }}>
          <button className="btn-o" onClick={exportPdf} disabled={pdfLoading}
            style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
            <FileText size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            {pdfLoading ? '出力中…' : 'PDF出力'}
          </button>
          <button className="btn-o" onClick={exportExcel} disabled={excelLoading}>
            <FileSpreadsheet size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            {excelLoading ? '出力中…' : 'Excel出力'}
          </button>
          <button className="btn-p" onClick={() => openNew()}>
            <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            配乗登録
          </button>
        </div>
      </div>

      {/* コントロール: 船舶セレクタ + 週ナビ */}
      <div className="flex-row" style={{ marginBottom: 16, justifyContent: 'space-between' }}>
        <div className="form-g" style={{ margin: 0 }}>
          <select className="form-i" value={vesselFilter} onChange={e => setVesselFilter(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">全船舶</option>
            {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
          </select>
        </div>
        <div className="flex-row" style={{ gap: 8 }}>
          <button className="btn-o" onClick={() => setWeekStart(w => addDays(w, -7))}>
            <ChevronLeft size={14} style={{ display: 'inline', verticalAlign: 'middle' }} /> 前週
          </button>
          <button className="btn-o" onClick={() => setWeekStart(startOfWeek(new Date()))}
            style={{ fontWeight: 700, minWidth: 130, justifyContent: 'center' }}>
            {rangeLabel}
          </button>
          <button className="btn-o" onClick={() => setWeekStart(w => addDays(w, 7))}>
            次週 <ChevronRight size={14} style={{ display: 'inline', verticalAlign: 'middle' }} />
          </button>
        </div>
      </div>

      {/* 休息規制の抵触予定パネル */}
      {violations.length > 0 && (
        <div className="card" style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#b45309', marginBottom: 8, fontSize: 14 }}>
            <AlertTriangle size={16} /> 休息規制の抵触予定（{violations.length}件）
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {violations.map((v, i) => (
              <div key={i} style={{ fontSize: 13, color: '#92400e' }}>
                <strong>{v.user_name}</strong>（{(v.shift_date ?? '').slice(0, 10)}）: {v.issue}
                {v.rest_hours != null && <span style={{ marginLeft: 6, color: '#b45309' }}>休息 {v.rest_hours}時間</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ minWidth: 820 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, background: '#FAFAFA', zIndex: 1, minWidth: 130 }}>クルー</th>
                {days.map((d, i) => {
                  const isToday = ymd(d) === ymd(new Date());
                  return (
                    <th key={i} style={{ textAlign: 'center', minWidth: 96, background: isToday ? '#eff6ff' : undefined }}>
                      {d.getMonth() + 1}/{d.getDate()}
                      <span style={{ marginLeft: 4, color: d.getDay() === 0 ? '#dc2626' : d.getDay() === 6 ? '#2563eb' : '#94a3b8' }}>
                        ({DOW[d.getDay()]})
                      </span>
                    </th>
                  );
                })}
              </tr>
              {/* 配乗人数サマリー行: 船舶ごとの当日配乗人数 */}
              <tr>
                <th style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1, fontSize: 10, fontWeight: 600, color: '#94a3b8', textAlign: 'left' }}>
                  <Ship size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />配乗人数
                </th>
                {days.map((_d, i) => {
                  const counts = manningByDate[i] ?? new Map<string, number>();
                  return (
                    <th key={i} style={{ background: '#fff', padding: '4px 2px', fontWeight: 400 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                        {counts.size === 0 ? (
                          <span style={{ fontSize: 10, color: '#cbd5e1' }}>-</span>
                        ) : [...counts.entries()].map(([vid, cnt]) => {
                          const v = vessels.find(x => x.id === vid);
                          const color = vesselColor(vid);
                          return (
                            <span key={vid} title={v?.vessel_name ?? ''} style={{ fontSize: 9, fontWeight: 700, color, background: `${color}1a`, border: `1px solid ${color}55`, borderRadius: 20, padding: '1px 6px' }}>
                              {cnt}
                            </span>
                          );
                        })}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {crewRows.length === 0 ? (
                <tr><td colSpan={8} className="empty">
                  {vesselFilter
                    ? 'この船舶に配乗中／配乗予定のクルーがいません（右上の「配乗登録」から追加できます）'
                    : '対象クルーがいません'}
                </td></tr>
              ) : crewRows.map(u => {
                const baseline = baselineOf(u.id);
                return (
                  <tr key={u.id}>
                    <td style={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{u.user_name}</div>
                      {!vesselFilter && baseline && (
                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>現在: {baseline.vessel_name}</div>
                      )}
                    </td>
                    {days.map((d, i) => {
                      const dateIso = ymd(d);
                      const s = shiftAt(u.id, dateIso);
                      if (s) {
                        const st = timeOf(s.planned_start_at), en = timeOf(s.planned_end_at);
                        if (s.shift_type === 'off') {
                          return (
                            <td key={i} style={{ padding: 6, textAlign: 'center', verticalAlign: 'middle' }}>
                              <button onClick={() => openEdit(s)} title={s.note ?? ''}
                                style={{ width: '100%', border: '1px solid #e2e8f0', background: '#f1f5f9', color: '#64748b', borderRadius: 8, padding: '6px 4px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                                休み
                              </button>
                            </td>
                          );
                        }
                        if (s.shift_type === 'standby') {
                          return (
                            <td key={i} style={{ padding: 6, textAlign: 'center', verticalAlign: 'middle' }}>
                              <button onClick={() => openEdit(s)} title={s.note ?? ''}
                                style={{ width: '100%', border: '1px solid #fde68a', background: '#fffbeb', color: '#d97706', borderRadius: 8, padding: '6px 4px', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                                <div>待機</div>
                                {(st || en) && <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{st}-{en}</div>}
                              </button>
                            </td>
                          );
                        }
                        // work: 配乗先の船舶をバッジ表示
                        const color = vesselColor(s.vessel_id);
                        return (
                          <td key={i} style={{ padding: 6, textAlign: 'center', verticalAlign: 'middle' }}>
                            <button onClick={() => openEdit(s)} title={s.note ?? ''}
                              style={{
                                width: '100%', border: `1px solid ${color}55`, background: `${color}14`, color,
                                borderRadius: 8, padding: '6px 4px', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                              }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.vessel_name ?? '船舶未設定'}
                              </div>
                              {(st || en) && <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{st}-{en}</div>}
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td key={i} style={{ padding: 6, textAlign: 'center' }}>
                          <button onClick={() => openNew(dateIso, u.id)}
                            style={{
                              width: '100%', border: '1px dashed #e2e8f0', background: 'transparent', color: '#cbd5e1',
                              borderRadius: 8, padding: '6px 4px', cursor: 'pointer', fontSize: 14, fontWeight: 700,
                            }}>+</button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 保存トースト */}
      {toast && (
        <div style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 1100, maxWidth: 380,
          background: toast.warnings.length ? '#fffbeb' : '#f0fdf4',
          border: `1px solid ${toast.warnings.length ? '#fde68a' : '#bbf7d0'}`,
          borderRadius: 12, padding: '14px 18px', boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: toast.warnings.length ? '#b45309' : '#059669' }}>{toast.msg}</div>
            <button onClick={() => setToast(null)} style={{ background: 'transparent', color: '#94a3b8', padding: 0 }}><X size={15} /></button>
          </div>
          {toast.warnings.map((w, i) => (
            <div key={i} style={{ marginTop: 8, fontSize: 12, color: '#92400e', display: 'flex', gap: 6 }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {w}
            </div>
          ))}
        </div>
      )}

      {/* 新規 / 編集モーダル */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460, width: '95%' }}>
            <h2 className="modal-title">{form.id ? '配乗予定の編集' : '配乗登録'}</h2>

            {modalError && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10, fontWeight: 600 }}>{modalError}</div>}

            {/* 保存成功だが休息警告あり */}
            {modalWarnings.length > 0 && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: '#b45309', marginBottom: 4 }}>保存しました（休息規制の警告）</div>
                {modalWarnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#92400e', display: 'flex', gap: 6, marginTop: 4 }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {w}
                  </div>
                ))}
              </div>
            )}

            <div className="form-g">
              <label className="form-l">クルー *</label>
              <SearchableSelect
                value={form.user_id}
                onChange={id => setForm(f => ({ ...f, user_id: id, vessel_id: f.vessel_id || baselineOf(id)?.vessel_id || '' }))}
                options={users.map(u => ({ value: u.id, label: u.user_name }))}
                placeholder="氏名で検索..."
              />
            </div>
            <div className="form-g">
              <label className="form-l">状態 *</label>
              <select className="form-i" value={form.shift_type} onChange={e => setForm(f => ({ ...f, shift_type: e.target.value as ShiftType }))}>
                <option value="work">乗船（船舶へ配乗）</option>
                <option value="standby">待機</option>
                <option value="off">休み</option>
              </select>
            </div>
            {form.shift_type === 'work' && (
              <div className="form-g">
                <label className="form-l">配乗船舶 *</label>
                <SearchableSelect
                  value={form.vessel_id}
                  onChange={id => setForm(f => ({ ...f, vessel_id: id }))}
                  options={vessels.map(v => ({ value: v.id, label: v.vessel_name }))}
                  placeholder="船舶名で検索..."
                />
              </div>
            )}
            <div className="form-g">
              <label className="form-l">日付 *</label>
              <input className="form-i" type="date" value={form.shift_date} onChange={e => setForm(f => ({ ...f, shift_date: e.target.value }))} />
            </div>
            <div className="flex-row" style={{ gap: 12 }}>
              <div className="form-g" style={{ margin: 0, flex: 1 }}>
                <label className="form-l">開始</label>
                <input className="form-i" type="time" value={form.start} onChange={e => setForm(f => ({ ...f, start: e.target.value }))} />
              </div>
              <div className="form-g" style={{ margin: 0, flex: 1 }}>
                <label className="form-l">終了</label>
                <input className="form-i" type="time" value={form.end} onChange={e => setForm(f => ({ ...f, end: e.target.value }))} />
              </div>
            </div>
            <div className="form-g">
              <label className="form-l">メモ</label>
              <input className="form-i" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="任意" />
            </div>

            <div className="flex-row" style={{ justifyContent: 'space-between', marginTop: 20 }}>
              <div>
                {form.id && (
                  <button className="btn-o" onClick={remove} disabled={saving}
                    style={{ color: '#dc2626', borderColor: '#fecaca' }}>
                    <Trash2 size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />削除
                  </button>
                )}
              </div>
              <div className="flex-row" style={{ gap: 8 }}>
                <button className="btn-o" onClick={() => setModalOpen(false)}>閉じる</button>
                <button className="btn-p" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Coming Soon 表示（配乗計画機能は準備中） ──────────────
// リリース時は下の CrewDeploymentPage の中身を `return <CrewDeploymentPageImpl />;` に戻す。
const CrewDeploymentPage: React.FC = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: 24 }}>
      <div style={{
        width: 72, height: 72, borderRadius: 20, background: '#eff6ff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24,
      }}>
        <CalendarClock size={34} color="#2563eb" />
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>配乗計画</div>
      <div style={{
        fontSize: 13, fontWeight: 700, color: '#2563eb', background: '#eff6ff',
        border: '1px solid #bfdbfe', borderRadius: 999, padding: '4px 16px', marginBottom: 16,
      }}>
        Coming Soon
      </div>
      <div style={{ fontSize: 14, color: '#64748b', textAlign: 'center', maxWidth: 420, lineHeight: 1.7 }}>
        この機能は現在準備中です。<br />
        公開まで今しばらくお待ちください。
      </div>
    </div>
  );
};

export default CrewDeploymentPage;
